import Cookies from "js-cookie"
import { useCallback, useState } from "react"

import toast from "@molecules/Toast/Toast"

export default function useAttachmentActions(endpointBase: string) {
    const [isDeleting, setIsDeleting] = useState(false)
    const [isTogglingVisibility, setIsTogglingVisibility] = useState(false)

    const deleteAttachment = useCallback(
        async (attachmentId: string): Promise<boolean> => {
            setIsDeleting(true)
            try {
                const response = await fetch(`${endpointBase}${attachmentId}/`, {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                    },
                    credentials: "include",
                })

                if (!response.ok) {
                    throw new Error(`Failed to delete attachment: ${response.statusText}`)
                }

                toast({ type: "success", title: "File deleted" })
                return true
            } catch (error) {
                console.error("Failed to delete attachment:", error)
                toast({ type: "error", title: "Could not delete file" })
                return false
            } finally {
                setIsDeleting(false)
            }
        },
        [endpointBase],
    )

    const toggleVisibility = useCallback(
        async (attachmentId: string, isCurrentlyPublic: boolean): Promise<Pick<Attachment, "is_public"> | null> => {
            const intendedVisibility = isCurrentlyPublic ? "private" : "public"

            setIsTogglingVisibility(true)
            try {
                const response = await fetch(`${endpointBase}${attachmentId}/`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                    },
                    credentials: "include",
                    body: JSON.stringify({ is_public: !isCurrentlyPublic }),
                })

                if (!response.ok) {
                    throw new Error(`Failed to update attachment: ${response.statusText}`)
                }

                const data = (await response.json()) as Pick<Attachment, "is_public">
                toast({ type: "success", title: `File is now ${intendedVisibility}` })
                return data
            } catch (error) {
                console.error("Failed to toggle visibility:", error)
                toast({ type: "error", title: `Could not make file ${intendedVisibility}` })
                return null
            } finally {
                setIsTogglingVisibility(false)
            }
        },
        [endpointBase],
    )

    return { deleteAttachment, toggleVisibility, isDeleting, isTogglingVisibility }
}
